import { PayloadAction, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  FeeManagementState,
  GlobalFeeSettings,
  VendorSpecificFee,
  FeeHistory,
  UpdateGlobalSettingsPayload,
  UpdateVendorFeePayload,
  CreateVendorFeePayload,
  RestaurantGlobalFees,
  RestaurantFeeHistoryItem,
} from "../../pages/Restaurants/FeeManagement/types";
import { feeService } from "../../pages/Restaurants/FeeManagement/services/feeService";
import {
  fetchRestaurantGlobalFees,
  updateRestaurantGlobalFees,
  fetchRestaurantSpecificFee,
  updateRestaurantSpecificFee,
  fetchRestaurantFeeHistory,
} from "../thunk";

// Get auth token
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  return jsonData?.token;
};

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Async thunks using fee service
export const fetchGlobalFeeSettings = createAsyncThunk(
  "feeManagement/fetchGlobalSettings",
  async (_, { rejectWithValue }) => {
    try {
      return await feeService.getGlobalFeeSettings();
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch global settings");
    }
  }
);

export const updateGlobalFeeSettings = createAsyncThunk(
  "feeManagement/updateGlobalSettings",
  async (payload: UpdateGlobalSettingsPayload, { rejectWithValue }) => {
    try {
      return await feeService.updateGlobalFeeSettings(payload);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update global settings");
    }
  }
);

export const fetchVendorSpecificFees = createAsyncThunk(
  "feeManagement/fetchVendorSpecificFees",
  async (_, { rejectWithValue }) => {
    try {
      return await feeService.getVendorSpecificFees();
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch vendor specific fees");
    }
  }
);

export const updateVendorSpecificFee = createAsyncThunk(
  "feeManagement/updateVendorSpecificFee",
  async (payload: UpdateVendorFeePayload, { rejectWithValue }) => {
    try {
      return await feeService.updateVendorSpecificFee(payload);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update vendor fee");
    }
  }
);

export const createVendorSpecificFee = createAsyncThunk(
  "feeManagement/createVendorSpecificFee",
  async (payload: CreateVendorFeePayload, { rejectWithValue }) => {
    try {
      return await feeService.createVendorSpecificFee(payload);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create vendor fee");
    }
  }
);

export const fetchFeeHistory = createAsyncThunk(
  "feeManagement/fetchFeeHistory",
  async (_, { rejectWithValue }) => {
    try {
      return await feeService.getFeeHistory();
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch fee history");
    }
  }
);

// Deprecated - keeping for compatibility
export const getFees = createAsyncThunk("getFees", async (_, thunkAPI) => {
  try {
    const token = getAuthToken();
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/get-allFees`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return res.data;
  } catch (error: any) {
    console.error(error);
    return thunkAPI.rejectWithValue(error.response.data);
  }
});

const initialState: FeeManagementState = {
  globalSettings: null,
  vendorSpecificFees: [],
  feeHistory: [],
  commissions: [],
  deliveryFees: [],
  serviceCharges: [],
  loading: false,
  error: null,
  currentTab: 'global',
  restaurantGlobalFees: null,
  restaurantFeeHistory: [],
};

const feeManagementSlice = createSlice({
  name: "feeManagement",
  initialState,
  reducers: {
    setCurrentTab: (state, action: PayloadAction<'global' | 'vendor' | 'history'>) => {
      state.currentTab = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Global Settings
      .addCase(fetchGlobalFeeSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGlobalFeeSettings.fulfilled, (state, action: PayloadAction<GlobalFeeSettings>) => {
        state.loading = false;
        state.globalSettings = action.payload;
      })
      .addCase(fetchGlobalFeeSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Global Settings
      .addCase(updateGlobalFeeSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGlobalFeeSettings.fulfilled, (state, action: PayloadAction<GlobalFeeSettings>) => {
        state.loading = false;
        state.globalSettings = action.payload;
      })
      .addCase(updateGlobalFeeSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Vendor Specific Fees
      .addCase(fetchVendorSpecificFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorSpecificFees.fulfilled, (state, action: PayloadAction<VendorSpecificFee[]>) => {
        state.loading = false;
        state.vendorSpecificFees = action.payload;
      })
      .addCase(fetchVendorSpecificFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Vendor Specific Fee
      .addCase(updateVendorSpecificFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVendorSpecificFee.fulfilled, (state, action: PayloadAction<VendorSpecificFee>) => {
        state.loading = false;
        const index = state.vendorSpecificFees.findIndex(fee => fee.restaurantId === action.payload.restaurantId);
        if (index !== -1) {
          state.vendorSpecificFees[index] = action.payload;
        }
      })
      .addCase(updateVendorSpecificFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create Vendor Specific Fee
      .addCase(createVendorSpecificFee.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVendorSpecificFee.fulfilled, (state, action: PayloadAction<VendorSpecificFee>) => {
        state.loading = false;
        state.vendorSpecificFees.push(action.payload);
      })
      .addCase(createVendorSpecificFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Fee History
      .addCase(fetchFeeHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFeeHistory.fulfilled, (state, action: PayloadAction<FeeHistory[]>) => {
        state.loading = false;
        state.feeHistory = action.payload;
      })
      .addCase(fetchFeeHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Deprecated getFees - keeping for compatibility
      .addCase(getFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFees.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        // Handle legacy format if needed
      })
      .addCase(getFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Unknown error";
      })
      // Restaurant Global Fees
      .addCase(fetchRestaurantGlobalFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurantGlobalFees.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload.success && action.payload.data) {
          state.restaurantGlobalFees = action.payload.data;
          // Map to global settings format for compatibility
          state.globalSettings = {
            id: action.payload.data._id,
            defaultCommissionRate: action.payload.data.commissionRate,
            defaultCommissionType: 'percentage',
            defaultBaseFee: action.payload.data.deliveryRate,
            defaultFeePerKM: 0,
            defaultFreeDistance: 0,
            defaultServiceChargeType: 'percentage',
            defaultServiceChargeValue: action.payload.data.serviceCharge,
            transactionFee: 0,
            setupFee: 0,
            monthlyFee: 0,
            updatedAt: action.payload.data.updatedAt,
            updatedBy: 'Admin',
          };
        }
      })
      .addCase(fetchRestaurantGlobalFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update Restaurant Global Fees
      .addCase(updateRestaurantGlobalFees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRestaurantGlobalFees.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload.success && action.payload.data) {
          state.restaurantGlobalFees = action.payload.data;
          // Update global settings
          if (state.globalSettings) {
            state.globalSettings.defaultCommissionRate = action.payload.data.commissionRate;
            state.globalSettings.defaultBaseFee = action.payload.data.deliveryRate;
            state.globalSettings.defaultServiceChargeValue = action.payload.data.serviceCharge;
            state.globalSettings.updatedAt = action.payload.data.updatedAt;
          }
        }
      })
      .addCase(updateRestaurantGlobalFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch Restaurant Fee History
      .addCase(fetchRestaurantFeeHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRestaurantFeeHistory.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        if (action.payload.success && action.payload.data) {
          state.restaurantFeeHistory = action.payload.data;
          // Map to feeHistory format for compatibility
          state.feeHistory = action.payload.data.map((item: RestaurantFeeHistoryItem) => ({
            id: item._id,
            changeType: item.metadata.scope === 'Global' ? 'global' : 
                       (item.metadata.feeType === 'commissionRate' ? 'commission' : 
                        item.metadata.feeType === 'deliveryRate' ? 'delivery' : 'service'),
            restaurantId: item.restaurantId,
            restaurantName: item.restaurantName,
            previousValue: item.previousValue,
            newValue: item.newValue,
            changedBy: 'Admin',
            changeReason: item.description,
            timestamp: item.createdAt,
            status: 'active' as const,
          }));
        }
      })
      .addCase(fetchRestaurantFeeHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentTab, clearError, resetState } = feeManagementSlice.actions;
export default feeManagementSlice.reducer;
